import React, { type ReactNode, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import {
  type ITextInputProps,
  TextInput,
} from "src/shared/forms/inputs/TextInput";
import { combineReduxFormValidationRules } from "src/shared/forms/validations/combineReduxFormValidationRules";

export interface NumberInputProps extends Omit<ITextInputProps, "type"> {
  isInteger?: boolean;
  min?: number;
  max?: number;
}

export const NumberInput = ({
  isInteger,
  validate,
  ...rest
}: NumberInputProps) => {
  const { min, max, validationDisabled } = rest;
  const validations = useMemo(() => {
    return combineReduxFormValidationRules(
      !isInteger &&
        ((value: string | number | undefined | null): ReactNode | void => {
          if (value === undefined || value === null) {
            return;
          }
          const valueNo = typeof value === "string" ? parseFloat(value) : value;
          if (Number.isNaN(valueNo)) {
            return <FormattedMessage id="form.validations.number.invalid" />;
          }
        }),
      isInteger &&
        ((value: string | number | undefined | null): ReactNode | void => {
          if (value === undefined || value === null) {
            return;
          }
          const valueNo = typeof value === "string" ? parseFloat(value) : value;
          if (!Number.isSafeInteger(valueNo)) {
            return (
              <FormattedMessage id="form.validations.number.integer.invalid" />
            );
          }
        }),
      !validationDisabled &&
        (min !== undefined || max !== undefined) &&
        ((value: string | number | undefined | null): ReactNode | void => {
          if (value === undefined || value === null) {
            return;
          }
          if (min !== undefined && value < min) {
            return (
              <FormattedMessage
                id="form.validations.number.min"
                values={{ min }}
              />
            );
          }
          if (max !== undefined && value > max) {
            return (
              <FormattedMessage
                id="form.validations.number.max"
                values={{ max }}
              />
            );
          }
        }),
      validate,
    );
  }, [isInteger, max, min, validate, validationDisabled]);
  return <TextInput {...rest} type="number" validate={validations} />;
};
